/** Mirrors backend `utils/userIdPattern.js` for UI preview. */

const QUOTE_CHARS = /["'`\u201c\u201d\u2018\u2019\u00ab\u00bb]/g;

export function stripQuotesAndSpaces(s) {
  return String(s || '')
    .trim()
    .replace(QUOTE_CHARS, '')
    .replace(/\s+/g, '');
}

export function slugPart(s) {
  return stripQuotesAndSpaces(s)
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
}

function optionalSlug(s) {
  if (s == null) return '';
  const t = stripQuotesAndSpaces(s);
  return t ? slugPart(t) : '';
}

function firstAlphaN(name, n) {
  const letters = String(name || '').replace(/[^a-zA-Z]/g, '');
  if (!letters || n < 1) return '';
  return letters.slice(0, Math.min(n, letters.length)).toLowerCase();
}

function patternFrag(row, snake, camel) {
  return row?.[snake] ?? row?.[camel] ?? '';
}

function buildReplacementPairs(firstName, lastName, domain, patternRow) {
  const fn = String(firstName || '').trim();
  const ln = String(lastName || '').trim();
  const fi = fn ? fn[0].toLowerCase() : '';
  const li = ln ? ln[0].toLowerCase() : '';
  const slugPrefix = optionalSlug(patternFrag(patternRow, 'prefix_string', 'prefixString'));
  const slugSuffix = optionalSlug(patternFrag(patternRow, 'suffix_string', 'suffixString'));
  const slugMiddle = optionalSlug(patternFrag(patternRow, 'middle_string', 'middleString'));
  return [
    ['{firstname_3}', firstAlphaN(fn, 3)],
    ['{firstname_2}', firstAlphaN(fn, 2)],
    ['{firstname_initial}', fi],
    ['{lastname_3}', firstAlphaN(ln, 3)],
    ['{lastname_2}', firstAlphaN(ln, 2)],
    ['{lastname_initial}', li],
    ['{companydomain}', domain],
    ['{domainname}', domain],
    ['{firstname}', slugPart(fn)],
    ['{lastname}', slugPart(ln)],
    ['{domain}', domain],
    ['{prefix}', slugPrefix],
    ['{suffix}', slugSuffix],
    ['{middle}', slugMiddle],
  ];
}

/** Same rules as backend `sanitizePatternTemplate` (throws on invalid chars). */
export function sanitizePatternTemplate(template) {
  if (!template || typeof template !== 'string') {
    throw new Error('Pattern template is required.');
  }
  let t = String(template).trim().replace(QUOTE_CHARS, '').replace(/\s+/g, '');
  if (!t) {
    throw new Error('Pattern template is empty.');
  }
  t = t.replace(/\{dev\}/gi, '{suffix}');
  if (/[^a-zA-Z0-9@._\-{}]/.test(t)) {
    const m = t.match(/[^a-zA-Z0-9@._\-{}]/);
    const ch = m ? m[0] : '?';
    throw new Error(
      `Pattern may only use letters, digits, @, ., -, _, and placeholders in braces (found ${JSON.stringify(ch)}).`
    );
  }
  return t;
}

export function previewProvisionedEmail(template, firstName, lastName, domain, patternRow) {
  if (!template) return '';
  let tpl;
  try {
    tpl = sanitizePatternTemplate(template);
  } catch {
    return '—';
  }
  const dom = String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^@+/, '')
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .split(':')[0];
  const pairs = buildReplacementPairs(firstName, lastName, dom, patternRow);
  let out = tpl;
  pairs.forEach(([k, val]) => {
    const re = new RegExp(k.replace(/[{}]/g, '\\$&'), 'gi');
    out = out.replace(re, val);
  });
  out = out.trim().toLowerCase();
  if (!out.includes('@')) return '—';
  const p = out.split('@');
  if (p.length !== 2 || !p[0] || !p[1]) return '—';

  const unk = p[0].match(/\{[^}]+\}/g);
  if (unk && unk.length) {
    return `Unsupported: ${unk.join(', ')} — use documented placeholders only.`;
  }
  let local = p[0].replace(/\.{2,}/g, '.').replace(/^\.+|\.+$/g, '');
  if (!local) return 'Empty local part (often empty {middle} between dots).';
  return `${local}@${p[1]}`;
}
